import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker, Select, Button, Spin, message, Radio } from 'antd';
import dayjs from 'dayjs';
import { fetchActivitiesByLocationPhysical, fetchPlanVersions, fetchEquipmentList } from './api';
import { PRODUCT_YIELD, DEFAULT_YIELD } from './constants/productYield';

const { RangePicker } = DatePicker;

const PhysicalVolumeSummary = () => {
  // Calculate default date/time range dynamically (same as Equipment Log)
  // Round current time UP to next hour, then go back 23 hours
  const calculateDefaultRange = () => {
    const now = new Date();
    const rounded = new Date(now);
    
    // Round UP to next hour (ceiling)
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    
    const end = dayjs(rounded);
    const start = end.subtract(23, 'hour');
    
    return [start, end];
  };

  // State management
  const [dateTimeRange, setDateTimeRange] = useState(() => calculateDefaultRange());
  const [summaryData, setSummaryData] = useState(null); // Equipment-keyed volume data from activitiesByLocation
  const [apiMeta, setApiMeta] = useState(null); // Store API meta (version info)
  const [loading, setLoading] = useState(false);
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedPlanVersion, setSelectedPlanVersion] = useState(null); // null means latest
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [equipmentList, setEquipmentList] = useState([]); // Full equipment list from API
  const [selectedMonth, setSelectedMonth] = useState(null); // For week picker
  const [selectedWeek, setSelectedWeek] = useState(null); // Selected week number
  const [filterMode, setFilterMode] = useState('date'); // 'date' or 'week'
  const [expandedAreas, setExpandedAreas] = useState(new Set()); // Track which areas are expanded
  const [activeTimeRange, setActiveTimeRange] = useState(null); // Quick time range selector

  // Handle quick time range selection
  const handleTimeRangeChange = (range) => {
    setActiveTimeRange(range);
    const now = dayjs();
    let start;
    switch (range) {
      case '1D':
        start = now.subtract(1, 'day');
        break;
      case '1W':
        start = now.subtract(1, 'week');
        break;
      case '1M':
        start = now.subtract(1, 'month');
        break;
      case '3M':
        start = now.subtract(3, 'month');
        break;
      case '6M':
        start = now.subtract(6, 'month');
        break;
      default:
        return;
    }
    setDateTimeRange([start, now]);
  };

  // Toggle area expansion
  const toggleAreaExpansion = (areaName) => {
    setExpandedAreas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(areaName)) {
        newSet.delete(areaName);
      } else {
        newSet.add(areaName);
      }
      return newSet;
    });
  };

  // Format date for display (matches Equipment Log page)
  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = dayjs.isDayjs(date) ? date.toDate() : (date instanceof Date ? date : dayjs(date).toDate());
    return dateObj.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Format time for display (matches Equipment Log page - 24-hour format)
  const formatTime = (date) => {
    if (!date) return '';
    const dateObj = dayjs.isDayjs(date) ? date.toDate() : (date instanceof Date ? date : dayjs(date).toDate());
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Equipment category mapping based on name prefix (same as ConsolidatedTracker)
  const getEquipmentCategory = (equipmentName) => {
    if (!equipmentName) return 'Other';
    const name = String(equipmentName).toUpperCase();
    
    // Order matters - check more specific patterns first
    if (name.startsWith('COMP-') || name.startsWith('COMP')) return 'Compression';
    if (name.startsWith('COAT-') || name.startsWith('COAT')) return 'Coating';
    if (name.startsWith('CAPS-') || name.startsWith('CAPS')) return 'Capsulation';
    if (name.startsWith('IMP-') || name.startsWith('IMP')) return 'Imprinting';
    if (name.startsWith('FBP-') || name.startsWith('FBP')) return 'FBP';
    // Packing machines (P1 Primary, P1 Secondary, ...)
    if (/^P\d+\s*(PRIMARY|SECONDARY)$/.test(name)) return 'Packing';
    if (name.match(/^B\d/)) return 'Blending'; // B1, B2, B3, etc.
    if (name.match(/^G\d/)) return 'Granulation'; // G1, G2, G3, etc.
    return 'Other';
  };

  // Group configuration with colors and display order (same as ConsolidatedTracker)
  const groupConfig = {
    'Granulation': { color: '#8b5cf6', bgColor: '#f5f3ff', borderColor: '#c4b5fd', icon: '🧪' },
    'FBP': { color: '#06b6d4', bgColor: '#ecfeff', borderColor: '#67e8f9', icon: '🌡️' },
    'Blending': { color: '#f59e0b', bgColor: '#fffbeb', borderColor: '#fcd34d', icon: '🔄' },
    'Compression': { color: '#3b82f6', bgColor: '#eff6ff', borderColor: '#93c5fd', icon: '⚙️' },
    'Coating': { color: '#10b981', bgColor: '#ecfdf5', borderColor: '#6ee7b7', icon: '🎨' },
    'Capsulation': { color: '#ec4899', bgColor: '#fdf2f8', borderColor: '#f9a8d4', icon: '💊' },
    'Imprinting': { color: '#6366f1', bgColor: '#eef2ff', borderColor: '#a5b4fc', icon: '✍️' },
    'Packing': { color: '#f97316', bgColor: '#fff7ed', borderColor: '#fdba74', icon: '📦' },
    'Other': { color: '#6b7280', bgColor: '#f9fafb', borderColor: '#d1d5db', icon: '📦' }
  };

  // Define group order
  const groupOrder = ['Granulation', 'FBP', 'Blending', 'Compression', 'Coating', 'Capsulation', 'Imprinting', 'Packing', 'Other'];

  // Calculate business weeks for a given month (weeks start on Friday)
  // Uses "majority days" rule: a week belongs to the month that has more of its days
  const getBusinessWeeksInMonth = useCallback((monthDate) => {
    if (!monthDate) return [];
    
    const month = dayjs(monthDate);
    // Month period: 26th of previous month → 25th of current month (BA 25th cutoff rule)
    const periodStart = month.subtract(1, 'month').date(26).startOf('day');
    const periodEnd = month.date(25).endOf('day');
    
    const weeks = [];
    let weekNumber = 1;
    
    // Find the first Friday on or before the period start
    let currentFriday = periodStart.day(5); // 5 = Friday
    if (currentFriday.isAfter(periodStart)) {
      currentFriday = currentFriday.subtract(7, 'day');
    }

    // Helper to count days of a week that fall within the month period (26th prev → 25th curr)
    const countDaysInPeriod = (weekStart, weekEnd) => {
      let count = 0;
      let day = weekStart;
      while (day.isBefore(weekEnd) || day.isSame(weekEnd, 'day')) {
        if ((day.isAfter(periodStart) || day.isSame(periodStart, 'day')) &&
            (day.isBefore(periodEnd) || day.isSame(periodEnd, 'day'))) {
          count++;
        }
        day = day.add(1, 'day');
      }
      return count;
    };

    // Generate weeks - include if majority days (>= 4 out of 7) fall in the month period
    while (currentFriday.isBefore(periodEnd.add(7, 'day'))) {
      const weekStart = currentFriday.startOf('day');
      const weekEnd = currentFriday.add(6, 'day').endOf('day'); // Friday to next Thursday
      
      const daysInSelectedMonth = countDaysInPeriod(weekStart, weekEnd.startOf('day'));
      
      // Include week if majority of its days (>= 4) fall in the month period
      if (daysInSelectedMonth >= 4) {
        weeks.push({
          weekNumber,
          label: `Week ${weekNumber} (${weekStart.format('DD MMM')} - ${weekEnd.format('DD MMM')})`,
          startDate: weekStart,
          endDate: weekEnd
        });
        weekNumber++;
      }
      
      currentFriday = currentFriday.add(7, 'day');
    }
    
    return weeks;
  }, []);

  // Handle week selection
  const handleWeekSelect = useCallback((weekValue) => {
    if (!selectedMonth) {
      setSelectedWeek(null);
      return;
    }
    
    // Handle "All Weeks" selection
    if (weekValue === 'all') {
      const month = dayjs(selectedMonth);
      // Period: 26th of previous month → 25th of current month
      setDateTimeRange([month.subtract(1, 'month').date(26).startOf('day'), month.date(25).endOf('day')]);
      setSelectedWeek('all');
      return;
    }
    
    // Handle clearing selection - default back to All Weeks
    if (!weekValue) {
      const month = dayjs(selectedMonth);
      // Period: 26th of previous month → 25th of current month
      setDateTimeRange([month.subtract(1, 'month').date(26).startOf('day'), month.date(25).endOf('day')]);
      setSelectedWeek('all');
      return;
    }
    
    const weeks = getBusinessWeeksInMonth(selectedMonth);
    const selectedWeekData = weeks.find(w => w.weekNumber === weekValue);
    
    if (selectedWeekData) {
      setDateTimeRange([selectedWeekData.startDate, selectedWeekData.endDate]);
      setSelectedWeek(weekValue);
    }
  }, [selectedMonth, getBusinessWeeksInMonth]);

  // Handle month change - defaults to "All Weeks" and sets full month date range
  const handleMonthChange = useCallback((month) => {
    setSelectedMonth(month);
    if (month) {
      const m = dayjs(month);
      // Period: 26th of previous month → 25th of current month
      setDateTimeRange([m.subtract(1, 'month').date(26).startOf('day'), m.date(25).endOf('day')]);
      setSelectedWeek('all'); // Default to All Weeks
    } else {
      setSelectedWeek(null);
    }
  }, []);

  // Get weeks for current selected month
  const availableWeeks = useMemo(() => {
    if (!selectedMonth) return [];
    return getBusinessWeeksInMonth(selectedMonth);
  }, [selectedMonth, getBusinessWeeksInMonth]);

  // Fetch plan versions on mount
  const loadPlanVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const response = await fetchPlanVersions();
      if (response && response.data) {
        setPlanVersions(response.data);
      }
    } catch (err) {
      console.error('Error fetching plan versions:', err);
    } finally {
      setLoadingVersions(false);
    }
  }, []);

  // Fetch equipment list on mount (same as ConsolidatedTracker)
  useEffect(() => {
    const loadEquipmentList = async () => {
      try {
        const response = await fetchEquipmentList();
        setEquipmentList(response.data || []);
      } catch (err) {
        console.error('Error loading equipment list:', err);
      }
    };
    loadEquipmentList();
  }, []);

  // Merge equipment list with summary data from activitiesByLocation API
  // Extract volumes from activities (same data structure as ConsolidatedTracker)
  const mergeEquipmentWithSummaryData = useMemo(() => {
    if (!equipmentList || equipmentList.length === 0) return [];
    
    // Get date range for filtering
    const rangeStart = dateTimeRange?.[0];
    const rangeEnd = dateTimeRange?.[1];
    
    // Build a map of equipment_name -> volume data from activities
    const volumeMap = {};
    
    if (summaryData && typeof summaryData === 'object') {
      Object.entries(summaryData).forEach(([equipmentName, equipmentInfo]) => {
        if (!equipmentInfo || typeof equipmentInfo !== 'object') return;

        // Skip SECONDARY packing equipment to avoid double counting volumes
        // (users log data in primary packing; secondary is excluded)
        const _upperName = String(equipmentName || '').toUpperCase();
        if (/^P\d+\s*SECONDARY$/.test(_upperName)) {
          volumeMap[equipmentName] = { planned: 0, actual: 0 };
          return;
        }

        // Use activity_timeline if available (like ConsolidatedTracker), otherwise fall back to arrays
        const activityTimeline = equipmentInfo.activity_timeline || [];
        const matched = equipmentInfo.matched || [];
        const notStarted = equipmentInfo.not_started || [];
        const unplanned = equipmentInfo.unplanned || [];

        const allActivities = activityTimeline.length > 0
          ? activityTimeline
          : [...matched, ...notStarted, ...unplanned];

        // Sum volumes from activities (exclude cleaning activities)
        let plannedVolume = 0;
        let actualVolume = 0;
        
        allActivities.forEach(activity => {
          // Skip cleaning activities (they don't have volumes)
          if (activity.cleaning_type || activity.activity_type === 'cleaning') {
            return;
          }

          // Check if either plan_start or plan_end is within selected range
          const planStartDayjs = activity.plan_start_date ? dayjs(activity.plan_start_date) : null;
          const planEndDayjs = activity.plan_end_date ? dayjs(activity.plan_end_date) : null;
          const isPlanInRange = (() => {
            if (!planStartDayjs && !planEndDayjs) return false;
            const startIn = planStartDayjs && !planStartDayjs.isBefore(rangeStart) && !planStartDayjs.isAfter(rangeEnd);
            const endIn = planEndDayjs && !planEndDayjs.isBefore(rangeStart) && !planEndDayjs.isAfter(rangeEnd);
            return !!(startIn || endIn);
          })();
          
          // Check if either actual_start or actual_end is within selected range
          const actualStartDayjs = activity.actual_start_time && activity.actual_start_time !== 'running'
            ? dayjs(activity.actual_start_time)
            : null;
          const actualEndDayjs = activity.actual_end_time && activity.actual_end_time !== 'running'
            ? dayjs(activity.actual_end_time)
            : null;
          const isActualInRange = (() => {
            if (!actualStartDayjs && !actualEndDayjs) return false;
            const startIn = actualStartDayjs && !actualStartDayjs.isBefore(rangeStart) && !actualStartDayjs.isAfter(rangeEnd);
            const endIn = actualEndDayjs && !actualEndDayjs.isBefore(rangeStart) && !actualEndDayjs.isAfter(rangeEnd);
            return !!(startIn || endIn);
          })();

          // Volume divisor for ESO/BUP/PANTO/ESC products
          const _prod = (activity.plan_product_name || activity.product_name || '').toUpperCase().trim();
          const _eq = String(equipmentName || '').toUpperCase();
          let _div = 1;
          if (_prod === 'ESO' && _eq.startsWith('FBP')) _div = 3;
          else if (_prod === 'BUP' && _eq.startsWith('COAT')) _div = 3;
          else if (_prod === 'PANTO' && _eq.startsWith('COAT')) _div = 2;
          else if (_prod === 'ESC' && /^B\d/.test(_eq)) _div = 2;
          const _yield = PRODUCT_YIELD[_prod] !== undefined ? PRODUCT_YIELD[_prod] : DEFAULT_YIELD;

          // Plan volume: matched + not_started, either plan date in range, use sliced value
          if ((activity.status === 'matched' || activity.status === 'not_started') && isPlanInRange) {
            const planVol = Math.round(((parseFloat(activity.sliced_planned_volume || 0) || 0) / _div) * _yield * 100) / 100;
            plannedVolume += planVol;
          }

          // Actual volume: matched + unplanned, either actual date in range, use sliced value
          if ((activity.status === 'matched' || activity.status === 'unplanned') && isActualInRange) {
            const rawActual = parseFloat(activity.sliced_actual_volume);
            if (!isNaN(rawActual)) {
              const actVol = Math.round((rawActual / _div) * _yield * 100) / 100;
              actualVolume += actVol;
            }
          }
        });
        
        volumeMap[equipmentName] = {
          planned: plannedVolume,
          actual: actualVolume
        };
      });
    }
    
    // Check if we have any data
    const hasData = Object.keys(volumeMap).length > 0;
    
    if (!hasData) {
      // No summary data - return all equipment with zeros
      return equipmentList
        .map(eq => {
          const eqpSequence = eq.eqp_sequence !== undefined && eq.eqp_sequence !== null 
            ? Number(eq.eqp_sequence) 
            : null;
          
          return {
            equipment_id: eq.equipment_id || eq.id,
            equipment_name: eq.equipment_name || eq.name,
            eqp_sequence: eqpSequence,
            planned_volume: 0,
            actual_volume: 0,
            variance: 0,
            percentage: 0,
            has_data: false
          };
        })
        .sort((a, b) => {
          if (a.eqp_sequence !== null && b.eqp_sequence !== null) {
            return a.eqp_sequence - b.eqp_sequence;
          }
          if (a.eqp_sequence !== null) return -1;
          if (b.eqp_sequence !== null) return 1;
          const nameA = a?.equipment_name != null ? String(a.equipment_name) : '';
          const nameB = b?.equipment_name != null ? String(b.equipment_name) : '';
          return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
        });
    }
    
    // Merge equipment list with volume data
    return equipmentList
      .map(eq => {
        const eqpSequence = eq.eqp_sequence !== undefined && eq.eqp_sequence !== null 
          ? Number(eq.eqp_sequence) 
          : null;
        
        const equipmentName = eq.equipment_name || eq.name;
        const volumeData = volumeMap[equipmentName];
        
        if (volumeData) {
          const variance = volumeData.actual - volumeData.planned;
          const percentage = volumeData.planned > 0 
            ? (volumeData.actual / volumeData.planned) * 100 
            : 0;
          
          return {
            equipment_id: eq.equipment_id || eq.id,
            equipment_name: equipmentName,
            eqp_sequence: eqpSequence,
            planned_volume: volumeData.planned,
            actual_volume: volumeData.actual,
            variance: variance,
            percentage: percentage,
            has_data: volumeData.planned > 0 || volumeData.actual > 0
          };
        } else {
          // Equipment not in summary - no activity
          return {
            equipment_id: eq.equipment_id || eq.id,
            equipment_name: equipmentName,
            eqp_sequence: eqpSequence,
            planned_volume: 0,
            actual_volume: 0,
            variance: 0,
            percentage: 0,
            has_data: false
          };
        }
      })
      .sort((a, b) => {
        // Same sorting as ConsolidatedTracker
        if (a.eqp_sequence !== null && b.eqp_sequence !== null) {
          return a.eqp_sequence - b.eqp_sequence;
        }
        if (a.eqp_sequence !== null) return -1;
        if (b.eqp_sequence !== null) return 1;
        const nameA = a?.equipment_name != null ? String(a.equipment_name) : '';
        const nameB = b?.equipment_name != null ? String(b.equipment_name) : '';
        return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: 'base' });
      });
  }, [equipmentList, summaryData, dateTimeRange]);

  // Group equipment by category
  const groupedEquipmentData = useMemo(() => {
    const groups = {};
    
    mergeEquipmentWithSummaryData.forEach(equipment => {
      const category = getEquipmentCategory(equipment.equipment_name);
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(equipment);
    });
    
    // Return ordered groups (only those with equipment)
    return groupOrder
      .filter(category => groups[category] && groups[category].length > 0)
      .map(category => ({
        category,
        config: groupConfig[category],
        equipment: groups[category]
      }));
  }, [mergeEquipmentWithSummaryData]);

  // Calculate Overall Summary (Blend Equivalents, Dose Equivalents, Un-coated tablets)
  // Derived from activity data grouped by equipment category
  const overallSummary = useMemo(() => {
    if (!summaryData || typeof summaryData !== 'object') return [];
    
    // Get date range for filtering
    const rangeStart = dateTimeRange?.[0];
    const rangeEnd = dateTimeRange?.[1];
    
    // Collect volumes and products by category
    const categoryStats = {};
    
    Object.entries(summaryData).forEach(([equipmentName, equipmentInfo]) => {
      if (!equipmentInfo || typeof equipmentInfo !== 'object') return;

      // Skip SECONDARY packing to avoid double counting volumes
      const _upperName = String(equipmentName || '').toUpperCase();
      if (/^P\d+\s*SECONDARY$/.test(_upperName)) return;

      const category = getEquipmentCategory(equipmentName);

      if (!categoryStats[category]) {
        categoryStats[category] = {
          planned: 0,
          actual: 0,
          productNames: new Set() // Product names, not batch numbers
        };
      }
      
      // Use activity_timeline if available
      const activityTimeline = equipmentInfo.activity_timeline || [];
      const matched = equipmentInfo.matched || [];
      const notStarted = equipmentInfo.not_started || [];
      const unplanned = equipmentInfo.unplanned || [];
      
      const allActivities = activityTimeline.length > 0 
        ? activityTimeline 
        : [...matched, ...notStarted, ...unplanned];
      
      allActivities.forEach(activity => {
        // Skip cleaning activities
        if (activity.cleaning_type || activity.activity_type === 'cleaning') return;

        // Check if either plan_start or plan_end is within selected range
        const planStartDayjs = activity.plan_start_date ? dayjs(activity.plan_start_date) : null;
        const planEndDayjs = activity.plan_end_date ? dayjs(activity.plan_end_date) : null;
        const isPlanInRange = (() => {
          if (!planStartDayjs && !planEndDayjs) return false;
          const startIn = planStartDayjs && !planStartDayjs.isBefore(rangeStart) && !planStartDayjs.isAfter(rangeEnd);
          const endIn = planEndDayjs && !planEndDayjs.isBefore(rangeStart) && !planEndDayjs.isAfter(rangeEnd);
          return !!(startIn || endIn);
        })();
        
        // Check if either actual_start or actual_end is within selected range
        const actualStartDayjs = activity.actual_start_time && activity.actual_start_time !== 'running'
          ? dayjs(activity.actual_start_time)
          : null;
        const actualEndDayjs = activity.actual_end_time && activity.actual_end_time !== 'running'
          ? dayjs(activity.actual_end_time)
          : null;
        const isActualInRange = (() => {
          if (!actualStartDayjs && !actualEndDayjs) return false;
          const startIn = actualStartDayjs && !actualStartDayjs.isBefore(rangeStart) && !actualStartDayjs.isAfter(rangeEnd);
          const endIn = actualEndDayjs && !actualEndDayjs.isBefore(rangeStart) && !actualEndDayjs.isAfter(rangeEnd);
          return !!(startIn || endIn);
        })();

        // Volume divisor for ESO/BUP/PANTO/ESC products
        const _prod = (activity.plan_product_name || activity.product_name || '').toUpperCase().trim();
        const _eq = String(equipmentName || '').toUpperCase();
        let _div = 1;
        if (_prod === 'ESO' && _eq.startsWith('FBP')) _div = 3;
        else if (_prod === 'BUP' && _eq.startsWith('COAT')) _div = 3;
        else if (_prod === 'PANTO' && _eq.startsWith('COAT')) _div = 2;
        else if (_prod === 'ESC' && /^B\d/.test(_eq)) _div = 2;
        const _yield = PRODUCT_YIELD[_prod] !== undefined ? PRODUCT_YIELD[_prod] : DEFAULT_YIELD;

        // Plan volume: matched + not_started, either plan date in range, use sliced value
        if ((activity.status === 'matched' || activity.status === 'not_started') && isPlanInRange) {
          const planVol = Math.round(((parseFloat(activity.sliced_planned_volume || 0) || 0) / _div) * _yield * 100) / 100;
          categoryStats[category].planned += planVol;
        }

        // Actual volume: matched + unplanned, either actual date in range, use sliced value
        if ((activity.status === 'matched' || activity.status === 'unplanned') && isActualInRange) {
          const rawActual = parseFloat(activity.sliced_actual_volume);
          if (!isNaN(rawActual)) {
            const actVol = Math.round((rawActual / _div) * _yield * 100) / 100;
            categoryStats[category].actual += actVol;
          }
        }

        // Collect product NAMES (not batch numbers)
        // plan_product_name or product_name contains the actual product name like CZX, FURO, etc.
        const productName = activity.plan_product_name || activity.product_name;
        if (productName) {
          categoryStats[category].productNames.add(productName);
        }
      });
    });
    
    // Define summary rows mapping
    // Blend Equivalents = Blending only
    // Un-coated tablets = Compression activities filtered to UNCOATED_PRODUCTS list only
    // Dose Equivalents = Coating + Capsulation + Un-coated tablets (filtered Compression)

    const UNCOATED_PRODUCTS = ['CZX', 'FURO', 'LVT', 'BUS', 'LORA', 'TIZA', 'TRAZ'];

    const blendCategories = ['Blending'];

    const calculateMetric = (categories) => {
      let planned = 0;
      let actual = 0;
      const productNames = new Set();
      categories.forEach(cat => {
        if (categoryStats[cat]) {
          planned += categoryStats[cat].planned;
          actual += categoryStats[cat].actual;
          categoryStats[cat].productNames.forEach(p => productNames.add(p));
        }
      });
      const percentage = planned > 0 ? Math.round((actual / planned) * 100) : 0;
      return { planned, actual, percentage, productNames: Array.from(productNames) };
    };

    // Un-coated tablets: Compression activities filtered by UNCOATED_PRODUCTS list
    let uncoatedPlanned = 0;
    let uncoatedActual = 0;
    const uncoatedProductNames = new Set();

    Object.entries(summaryData).forEach(([equipmentName, equipmentInfo]) => {
      if (!equipmentInfo || typeof equipmentInfo !== 'object') return;
      if (getEquipmentCategory(equipmentName) !== 'Compression') return;

      const activityTimeline = equipmentInfo.activity_timeline || [];
      const matched = equipmentInfo.matched || [];
      const notStarted = equipmentInfo.not_started || [];
      const unplanned = equipmentInfo.unplanned || [];
      const allActivities = activityTimeline.length > 0
        ? activityTimeline
        : [...matched, ...notStarted, ...unplanned];

      allActivities.forEach(activity => {
        if (activity.cleaning_type || activity.activity_type === 'cleaning') return;
        const productName = activity.plan_product_name || activity.product_name || '';
        const isUncoated = UNCOATED_PRODUCTS.some(p =>
          productName.toUpperCase().includes(p.toUpperCase())
        );
        if (!isUncoated) return;

        // Check if either plan_start or plan_end is within selected range
        const planStartDayjs = activity.plan_start_date ? dayjs(activity.plan_start_date) : null;
        const planEndDayjs = activity.plan_end_date ? dayjs(activity.plan_end_date) : null;
        const isPlanInRange = (() => {
          if (!planStartDayjs && !planEndDayjs) return false;
          const startIn = planStartDayjs && !planStartDayjs.isBefore(rangeStart) && !planStartDayjs.isAfter(rangeEnd);
          const endIn = planEndDayjs && !planEndDayjs.isBefore(rangeStart) && !planEndDayjs.isAfter(rangeEnd);
          return !!(startIn || endIn);
        })();
        
        // Check if either actual_start or actual_end is within selected range
        const actualStartDayjs = activity.actual_start_time && activity.actual_start_time !== 'running'
          ? dayjs(activity.actual_start_time)
          : null;
        const actualEndDayjs = activity.actual_end_time && activity.actual_end_time !== 'running'
          ? dayjs(activity.actual_end_time)
          : null;
        const isActualInRange = (() => {
          if (!actualStartDayjs && !actualEndDayjs) return false;
          const startIn = actualStartDayjs && !actualStartDayjs.isBefore(rangeStart) && !actualStartDayjs.isAfter(rangeEnd);
          const endIn = actualEndDayjs && !actualEndDayjs.isBefore(rangeStart) && !actualEndDayjs.isAfter(rangeEnd);
          return !!(startIn || endIn);
        })();

        // Volume divisor for ESO/BUP/PANTO/ESC products
        const _prod = (activity.plan_product_name || activity.product_name || '').toUpperCase().trim();
        const _eq = String(equipmentName || '').toUpperCase();
        let _div = 1;
        if (_prod === 'ESO' && _eq.startsWith('FBP')) _div = 3;
        else if (_prod === 'BUP' && _eq.startsWith('COAT')) _div = 3;
        else if (_prod === 'PANTO' && _eq.startsWith('COAT')) _div = 2;
        else if (_prod === 'ESC' && /^B\d/.test(_eq)) _div = 2;
        const _yield = PRODUCT_YIELD[_prod] !== undefined ? PRODUCT_YIELD[_prod] : DEFAULT_YIELD;

        if ((activity.status === 'matched' || activity.status === 'not_started') && isPlanInRange) {
          uncoatedPlanned += Math.round(((parseFloat(activity.sliced_planned_volume || 0) || 0) / _div) * _yield * 100) / 100;
        }
        if ((activity.status === 'matched' || activity.status === 'unplanned') && isActualInRange) {
          const rawActual = parseFloat(activity.sliced_actual_volume);
          if (!isNaN(rawActual)) {
            uncoatedActual += Math.round((rawActual / _div) * _yield * 100) / 100;
          }
        }
        uncoatedProductNames.add(productName);
      });
    });

    const uncoatedStats = {
      planned: uncoatedPlanned,
      actual: uncoatedActual,
      percentage: uncoatedPlanned > 0 ? Math.round((uncoatedActual / uncoatedPlanned) * 100) : 0,
      productNames: Array.from(uncoatedProductNames)
    };

    // Dose Equivalents = Coating + Capsulation + Un-coated tablets (filtered Compression)
    const doseBase = calculateMetric(['Coating', 'Capsulation']);
    const doseStats = {
      planned: doseBase.planned + uncoatedStats.planned,
      actual: doseBase.actual + uncoatedStats.actual,
      percentage: (doseBase.planned + uncoatedStats.planned) > 0
        ? Math.round(((doseBase.actual + uncoatedStats.actual) / (doseBase.planned + uncoatedStats.planned)) * 100)
        : 0
    };

    const blendStats = calculateMetric(blendCategories);
    
    // Only include rows that have data
    const summaryRows = [];
    
    if (blendStats.planned > 0 || blendStats.actual > 0) {
      // Show categories in UPPERCASE
      const activeBlendCategories = blendCategories
        .filter(c => categoryStats[c]?.planned > 0 || categoryStats[c]?.actual > 0)
        .map(c => c.toUpperCase());
      summaryRows.push({
        name: 'Blend Equivalents',
        planned: blendStats.planned.toFixed(1),
        actual: blendStats.actual.toFixed(1),
        percentage: blendStats.percentage,
        formula: 'Blending area volumes only',
        components: activeBlendCategories
      });
    }
    
    if (doseStats.planned > 0 || doseStats.actual > 0) {
      // Show categories in UPPERCASE + "Un-coated tablets" if filtered Compression has data
      const activeDoseCategories = ['Coating', 'Capsulation']
        .filter(c => categoryStats[c]?.planned > 0 || categoryStats[c]?.actual > 0)
        .map(c => c.toUpperCase());
      // Add "Un-coated tablets" only if filtered uncoated products have data
      if (uncoatedStats.planned > 0 || uncoatedStats.actual > 0) {
        activeDoseCategories.push('Un-coated tablets');
      }
      summaryRows.push({
        name: 'Dose Equivalents',
        planned: doseStats.planned.toFixed(1),
        actual: doseStats.actual.toFixed(1),
        percentage: doseStats.percentage,
        formula: 'Coating + Capsulation + Un-coated tablets',
        components: activeDoseCategories
      });
    }
    
    if (uncoatedStats.planned > 0 || uncoatedStats.actual > 0) {
      summaryRows.push({
        name: 'Un-coated tablets',
        planned: uncoatedStats.planned.toFixed(1),
        actual: uncoatedStats.actual.toFixed(1),
        percentage: uncoatedStats.percentage,
        formula: 'Compression area | Products: CZX, FURO, LVT, BUS, LORA, TIZA, TRAZ',
        components: uncoatedStats.productNames.slice(0, 10)
      });
    }
    
    return summaryRows;
  }, [summaryData, dateTimeRange]);

  // Fetch summary data using activitiesByLocation API (same as ConsolidatedTracker)
  const fetchSummaryData = useCallback(async () => {
    if (!dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]) {
      message.warning('Please select a date range first');
      return;
    }

    setLoading(true);
    
    try {
      const startDate = dateTimeRange[0].format('YYYY-MM-DD HH:mm:ss');
      const endDate = dateTimeRange[1].format('YYYY-MM-DD HH:mm:ss');
      
      const apiParams = {
        start_date: startDate,
        end_date: endDate
      };
      
      if (selectedPlanVersion !== null) {
        apiParams.plan_version = selectedPlanVersion;
      }
      
      const response = await fetchActivitiesByLocationPhysical(apiParams);
      
      if (response.success) {
        // Store equipment data (same structure as ConsolidatedTracker)
        setSummaryData(response.data || {});
        
        // Store meta for version info
        if (response.meta) {
          setApiMeta(response.meta);
        }
      } else {
        message.error('Failed to fetch summary data');
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
      message.error(`Failed to load summary: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [dateTimeRange, selectedPlanVersion]);

  // Export summary to Excel
  const handleExportSummaryToExcel = useCallback(() => {
    if (groupedEquipmentData.length === 0) {
      message.warning('No summary data to export');
      return;
    }

    setExporting(true);
    try {
      // Format date range for headers
      const startDateStr = dateTimeRange?.[0]?.format('DD-MMM-YY HH:mm') || 'Start';
      const endDateStr = dateTimeRange?.[1]?.format('DD-MMM-YY HH:mm') || 'End';
      const dateRangeHeader = `${startDateStr} to ${endDateStr}`;
      
      // Helper function to escape CSV values
      const escapeCsv = (value) => {
        if (value === null || value === undefined) return '';
        const str = String(value);
        if (str.includes(',') || str.includes('"') || str.includes('\n')) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };
      
      // Build CSV rows
      const csvRows = [];
      
      // ========== SECTION 1: Header with time range ==========
      csvRows.push(['Physical Plan VS Actual Volume Summary'].join(','));
      csvRows.push([`Time range: ${dateRangeHeader}`].join(','));
      if (apiMeta?.version_used) {
        csvRows.push([`Plan Version: v${apiMeta.version_used}`].join(','));
      }
      csvRows.push([''].join(',')); // Empty row
      
      // ========== SECTION 2: Overall Summary ==========
      if (overallSummary.length > 0) {
        csvRows.push(['Overall Summary'].join(','));
        csvRows.push([''].join(',')); // Empty row
        csvRows.push(['Metric', 'Planned', 'Actual', 'Percentage', 'Components/Products'].join(','));
        
        overallSummary.forEach(row => {
          csvRows.push([
            escapeCsv(row.name),
            escapeCsv(row.planned),
            escapeCsv(row.actual),
            escapeCsv(`${row.percentage}%`),
            escapeCsv(row.components.join(', '))
          ].join(','));
        });
        
        csvRows.push([''].join(',')); // Empty row
        csvRows.push([''].join(',')); // Empty row
      }
      
      // ========== SECTION 3: Area-wise Summary ==========
      if (groupedEquipmentData.length > 0) {
        csvRows.push(['Area-wise Summary & Equipment Details'].join(','));
        csvRows.push([''].join(',')); // Empty row
        
        // Area summary header
        csvRows.push(['Area', 'Planned', 'Actual', 'Variance', 'Percentage', 'Equipment Count (Active/Total)'].join(','));
        
        // Area-wise aggregated data
        groupedEquipmentData.forEach(group => {
          const totalPlanned = group.equipment.reduce((sum, eq) => sum + (eq.planned_volume || 0), 0);
          const totalActual = group.equipment.reduce((sum, eq) => sum + (eq.actual_volume || 0), 0);
          const totalVariance = totalActual - totalPlanned;
          const percentageValue = totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0;
          const activeEquipmentCount = group.equipment.filter(eq => eq.has_data).length;
          
          csvRows.push([
            escapeCsv(group.category),
            escapeCsv(totalPlanned.toFixed(2)),
            escapeCsv(totalActual.toFixed(2)),
            escapeCsv((totalVariance > 0 ? '+' : '') + totalVariance.toFixed(2)),
            escapeCsv(percentageValue.toFixed(2) + '%'),
            escapeCsv(`${activeEquipmentCount}/${group.equipment.length}`)
          ].join(','));
        });
        
        csvRows.push([''].join(',')); // Empty row
        csvRows.push([''].join(',')); // Empty row
        
        // ========== SECTION 4: Equipment Details by Area ==========
        csvRows.push(['Equipment Details by Area'].join(','));
        csvRows.push([''].join(',')); // Empty row
        
        // Equipment details header
        csvRows.push(['Area', 'Equipment', 'Planned', 'Actual', 'Variance', 'Percentage', 'Status'].join(','));
        
        // Equipment details data
        groupedEquipmentData.forEach(group => {
          group.equipment.forEach((eq, idx) => {
            csvRows.push([
              escapeCsv(idx === 0 ? group.category : ''), // Area name only on first equipment
              escapeCsv(eq.equipment_name),
              escapeCsv((eq.planned_volume || 0).toFixed(2)),
              escapeCsv((eq.actual_volume || 0).toFixed(2)),
              escapeCsv((eq.variance > 0 ? '+' : '') + (eq.variance || 0).toFixed(2)),
              escapeCsv((eq.percentage || 0).toFixed(2) + '%'),
              escapeCsv(eq.has_data ? 'Active' : 'No Activity')
            ].join(','));
          });
          
          // Empty row between areas
          csvRows.push([''].join(','));
        });
      }
      
      // Create CSV content
      const csvContent = csvRows.join('\n');
      
      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date range
      const fileStartDate = dateTimeRange?.[0]?.format('YYYY-MM-DD') || 'start';
      const fileEndDate = dateTimeRange?.[1]?.format('YYYY-MM-DD') || 'end';
      link.download = `Physical_Plan_vs_Actual_Summary_${fileStartDate}_to_${fileEndDate}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      message.success('Summary exported successfully!');
    } catch (err) {
      console.error('Error exporting summary:', err);
      message.error(`Failed to export summary: ${err.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  }, [apiMeta, dateTimeRange, groupedEquipmentData, overallSummary]);

  // Fetch plan versions on mount
  useEffect(() => {
    loadPlanVersions();
  }, [loadPlanVersions]);

  // Fetch data on mount and when filters change
  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  return (
    <div style={{ 
      padding: '12px 16px', 
      fontFamily: 'system-ui, -apple-system, sans-serif',
      backgroundColor: '#f9fafb',
      minHeight: '100vh'
    }}>
      {/* Combined Header + Controls */}
      <div style={{ 
        marginBottom: '12px',
        backgroundColor: 'white',
        padding: '10px 16px',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
      }}>
        {/* Title + Date Info Row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, fontSize: '17px', fontWeight: '600', whiteSpace: 'nowrap' }}>
            📊 Volume Summary (A) - Physical Log
          </h1>
          <span style={{ color: '#6b7280', fontSize: '12px' }}>
            {formatDate(dateTimeRange?.[0])} {formatTime(dateTimeRange?.[0])} → {formatDate(dateTimeRange?.[1])} {formatTime(dateTimeRange?.[1])}
          </span>
          {selectedWeek && selectedMonth && (
            <span style={{ 
              backgroundColor: '#dbeafe', 
              color: '#1e40af', 
              padding: '2px 8px', 
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500'
            }}>
              {selectedMonth.format('MMM YYYY')} - Week {selectedWeek}
            </span>
          )}
        </div>

        {/* Controls Row */}
        <div style={{ 
          display: 'flex',
          gap: '16px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
        {/* Filter Mode Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Filter By:</label>
          <Radio.Group 
            value={filterMode} 
            onChange={(e) => {
              setFilterMode(e.target.value);
              if (e.target.value === 'date') {
                // Reset week filter when switching to date mode
                setSelectedWeek(null);
                setSelectedMonth(null);
              } else if (e.target.value === 'week') {
                // Set to current month and week when switching to week mode
                const now = dayjs();
                setSelectedMonth(now);
                
                // Calculate weeks for current month
                const weeks = getBusinessWeeksInMonth(now);
                
                // Find which week contains today
                const currentWeek = weeks.find(w => 
                  !now.isBefore(w.startDate, 'day') && !now.isAfter(w.endDate, 'day')
                );
                
                if (currentWeek) {
                  setSelectedWeek(currentWeek.weekNumber);
                  setDateTimeRange([currentWeek.startDate, currentWeek.endDate]);
                } else if (weeks.length > 0) {
                  // Fallback to first week if current date not in any week
                  setSelectedWeek(weeks[0].weekNumber);
                  setDateTimeRange([weeks[0].startDate, weeks[0].endDate]);
                }
              }
            }}
            buttonStyle="solid"
          >
            <Radio.Button value="date">📅 Date Range</Radio.Button>
            <Radio.Button value="week">📆 Week</Radio.Button>
          </Radio.Group>
        </div>

        {/* Date/Time Range Picker */}
        {filterMode === 'date' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Date Range:</label>
            <RangePicker
              showTime={{ format: 'HH:mm', minuteStep: 5 }}
              format="DD-MMM-YY HH:mm"
              value={dateTimeRange}
              onChange={(dates) => {
                setDateTimeRange(dates);
                setActiveTimeRange(null);
              }}
              style={{ minWidth: '340px' }}
              allowClear={false}
              presets={[
                { label: 'Last 24 Hours', value: [dayjs().subtract(24, 'hours'), dayjs()] },
                { label: 'Last 7 Days', value: [dayjs().subtract(7, 'day').startOf('day'), dayjs()] },
                { label: 'Last 30 Days', value: [dayjs().subtract(30, 'day').startOf('day'), dayjs()] },
                { label: 'This Week', value: [dayjs().startOf('week'), dayjs()] },
                { label: 'This Month', value: [dayjs().startOf('month'), dayjs()] },
              ]}
            />
            {/* Quick Time Range Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '4px', 
              padding: '2px', 
              background: '#f7fafc', 
              borderRadius: '6px', 
              border: '1px solid #e2e8f0',
              marginLeft: '8px'
            }}>
              {['1D', '1W', '1M', '3M', '6M'].map((range) => (
                <button
                  key={range}
                  style={{
                    padding: '4px 10px',
                    border: 'none',
                    background: activeTimeRange === range ? '#2c5282' : 'transparent',
                    color: activeTimeRange === range ? 'white' : '#718096',
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    transition: 'all 0.2s ease',
                    boxShadow: activeTimeRange === range ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
                  }}
                  onClick={() => handleTimeRangeChange(range)}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Week Filter */}
        {filterMode === 'week' && (
          <>
            {/* Month Picker */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Month:</label>
              <DatePicker
                picker="month"
                value={selectedMonth}
                onChange={handleMonthChange}
                format="MMM YYYY"
                style={{ minWidth: '140px' }}
                placeholder="Select month"
              />
            </div>

            {/* Week Picker */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Week:</label>
              <Select
                value={selectedWeek}
                onChange={handleWeekSelect}
                disabled={!selectedMonth || availableWeeks.length === 0}
                style={{ minWidth: '260px' }}
                placeholder={!selectedMonth ? "Select month first" : "Select week"}
                options={[
                  { value: 'all', label: 'All Weeks (Full Month)' },
                  ...availableWeeks.map(w => ({
                    value: w.weekNumber,
                    label: w.label
                  }))
                ]}
              />
              {selectedMonth && (
                <span style={{ 
                  fontSize: '11px', 
                  color: '#6b7280',
                  fontStyle: 'italic'
                }}>
                  (Weeks start Friday)
                </span>
              )}
            </div>
          </>
        )}

        {/* Plan Version Selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontWeight: '500', color: '#374151', fontSize: '14px' }}>Plan Version:</label>
          <Select
            value={selectedPlanVersion}
            onChange={(value) => setSelectedPlanVersion(value)}
            loading={loadingVersions}
            style={{ minWidth: '180px' }}
            options={[
              { 
                value: null, 
                label: `Latest (v${apiMeta?.latest_version || planVersions[0]?.plan_version || '?'})` 
              },
              ...planVersions.map(v => ({
                value: v.plan_version,
                label: `v${v.plan_version} (${dayjs(v.published_at).format('DD-MMM HH:mm')})`
              }))
            ]}
          />
        </div>

        {/* Action Buttons */}
        <div style={{ 
          marginLeft: 'auto',
          display: 'flex',
          gap: '8px'
        }}>
          <Button
            type="primary"
            onClick={fetchSummaryData}
            disabled={loading || !dateTimeRange || !dateTimeRange[0] || !dateTimeRange[1]}
            loading={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {loading ? 'Loading...' : '🔄 Refresh Data'}
          </Button>

          <Button
            type="primary"
            icon={<span>📥</span>}
            onClick={handleExportSummaryToExcel}
            disabled={groupedEquipmentData.length === 0 || exporting}
            loading={exporting}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              backgroundColor: '#10b981',
              borderColor: '#10b981'
            }}
          >
            {exporting ? 'Exporting...' : 'Export to Excel'}
          </Button>
        </div>
        </div>{/* Controls Row */}
      </div>{/* Combined Header + Controls Card */}

      {/* Content */}
      <div style={{ 
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        padding: '12px 16px'
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
            <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading summary data...</p>
          </div>
        ) : groupedEquipmentData.length > 0 ? (
          <div style={{ maxHeight: 'calc(100vh - 210px)', overflowY: 'auto' }}>

            {/* Overall Summary Section - Calculated from activity data */}
            {overallSummary.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  📈 Overall Summary
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Metric</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Planned</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Actual</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>%</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Components/Products</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overallSummary.map((row, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: '500' }}>{row.name}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{row.planned}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{row.actual}</td>
                        <td style={{ 
                          padding: '10px 12px', 
                          borderBottom: '1px solid #e5e7eb', 
                          textAlign: 'center',
                          fontWeight: '600',
                          color: row.percentage >= 100 ? '#166534' : row.percentage > 0 ? '#ca8a04' : '#991b1b'
                        }}>
                          {row.percentage}%
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb' }}>
                          <div style={{ fontSize: '11px', color: '#9ca3af', fontStyle: 'italic' }}>{row.formula}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Location Area Summary Section */}
            {/* {summaryData.data.location_area_summary && summaryData.data.location_area_summary.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  🏭 Location Area Summary
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Area</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Planned</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Actual</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>%</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600' }}>Locations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryData.data.location_area_summary.map((area, idx) => (
                      <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontWeight: '500' }}>{area.name}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{area.planned}</td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', textAlign: 'center' }}>{area.actual}</td>
                        <td style={{ 
                          padding: '10px 12px', 
                          borderBottom: '1px solid #e5e7eb', 
                          textAlign: 'center',
                          fontWeight: '600',
                          color: area.percentage >= 100 ? '#166534' : area.percentage > 0 ? '#ca8a04' : '#991b1b'
                        }}>
                          {area.percentage}%
                        </td>
                        <td style={{ padding: '10px 12px', borderBottom: '1px solid #e5e7eb', fontSize: '12px', color: '#6b7280' }}>
                          {(area.locations || []).join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )} */}

            {/* Area-wise Summary with Expandable Equipment Details */}
            {groupedEquipmentData.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                  📊 Area-wise Summary & Equipment Details
                </h3>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f3f4f6' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', width: '30px', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}></th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}>Area</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}>Planned</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}>Actual</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}>Variance</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}>%</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '2px solid #e5e7eb', fontWeight: '600', position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#f3f4f6' }}>Equipment Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedEquipmentData.map((group, idx) => {
                      // Calculate totals for this area
                      const totalPlanned = group.equipment.reduce((sum, eq) => sum + (eq.planned_volume || 0), 0);
                      const totalActual = group.equipment.reduce((sum, eq) => sum + (eq.actual_volume || 0), 0);
                      const totalVariance = totalActual - totalPlanned;
                      const percentageValue = totalPlanned > 0 ? (totalActual / totalPlanned) * 100 : 0;
                      const percentage = percentageValue.toFixed(2);
                      const activeEquipmentCount = group.equipment.filter(eq => eq.has_data).length;
                      const isExpanded = expandedAreas.has(group.category);
                      
                      return (
                        <React.Fragment key={group.category}>
                          {/* Area Summary Row with Expand Button */}
                          <tr 
                            style={{ 
                              backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f9fafb',
                              cursor: 'pointer'
                            }}
                            onClick={() => toggleAreaExpansion(group.category)}
                          >
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb',
                              textAlign: 'center'
                            }}>
                              <span style={{ 
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: group.config.color,
                                userSelect: 'none'
                              }}>
                                {isExpanded ? '−' : '+'}
                              </span>
                            </td>
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb', 
                              borderLeft: `4px solid ${group.config.color}`,
                              fontWeight: '500'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>{group.category}</span>
                              </div>
                            </td>
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb', 
                              textAlign: 'center',
                              fontWeight: '500'
                            }}>
                              {totalPlanned.toFixed(2)}
                            </td>
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb', 
                              textAlign: 'center',
                              fontWeight: '500'
                            }}>
                              {totalActual.toFixed(2)}
                            </td>
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb', 
                              textAlign: 'center',
                              fontWeight: '600',
                              backgroundColor: totalVariance > 0 ? '#dcfce7' : totalVariance < 0 ? '#fee2e2' : 'transparent',
                              color: totalVariance > 0 ? '#166534' : totalVariance < 0 ? '#991b1b' : '#6b7280'
                            }}>
                              {totalVariance > 0 ? `+${totalVariance.toFixed(2)}` : totalVariance.toFixed(2)}
                            </td>
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb', 
                              textAlign: 'center',
                              fontWeight: '600',
                              color: percentageValue >= 100 ? '#166534' : percentageValue > 0 ? '#ca8a04' : '#991b1b'
                            }}>
                              {percentage}%
                            </td>
                            <td style={{ 
                              padding: '10px 12px', 
                              borderBottom: '1px solid #e5e7eb', 
                              textAlign: 'center',
                              fontSize: '12px',
                              color: '#6b7280'
                            }}>
                              {activeEquipmentCount}/{group.equipment.length}
                              <span style={{ fontSize: '11px', marginLeft: '4px', fontStyle: 'italic' }}>active</span>
                            </td>
                          </tr>

                          {/* Expanded Equipment Details */}
                          {isExpanded && group.equipment.map((eq, eqIdx) => {
                            // Variance styling
                            const getVarianceStyle = () => {
                              if (!eq.has_data) return { color: '#9ca3af' };
                              if (eq.variance > 0) return { backgroundColor: '#dcfce7', color: '#166534', fontWeight: '600' };
                              if (eq.variance < 0) return { backgroundColor: '#fee2e2', color: '#991b1b', fontWeight: '600' };
                              if (eq.variance === 0 && eq.planned_volume > 0) return { backgroundColor: '#fef3c7', color: '#92400e', fontWeight: '600' };
                              return {};
                            };
                            
                            return (
                              <tr 
                                key={eq.equipment_id} 
                                style={{ 
                                  backgroundColor: group.config.bgColor,
                                  opacity: eq.has_data ? 1 : 0.7
                                }}
                              >
                                <td style={{ 
                                  padding: '8px 12px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb'
                                }}></td>
                                <td style={{ 
                                  padding: '8px 12px 8px 28px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb',
                                  borderLeft: `3px solid ${group.config.color}`,
                                  fontWeight: '500',
                                  fontSize: '12px'
                                }}>
                                  <span style={{ marginRight: '8px', color: '#9ca3af' }}>↳</span>
                                  {eq.equipment_name}
                                  {!eq.has_data && (
                                    <span style={{ 
                                      marginLeft: '8px',
                                      fontSize: '10px',
                                      padding: '2px 6px',
                                      backgroundColor: '#e5e7eb',
                                      color: '#6b7280',
                                      borderRadius: '8px',
                                      fontWeight: '500'
                                    }}>
                                      No Activity
                                    </span>
                                  )}
                                </td>
                                <td style={{ 
                                  padding: '8px 12px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb', 
                                  textAlign: 'center',
                                  color: eq.has_data ? '#111827' : '#9ca3af',
                                  fontWeight: '500',
                                  fontSize: '12px'
                                }}>
                                  {(eq.planned_volume || 0).toFixed(2)}
                                </td>
                                <td style={{ 
                                  padding: '8px 12px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb', 
                                  textAlign: 'center',
                                  color: eq.has_data ? '#111827' : '#9ca3af',
                                  fontWeight: '500',
                                  fontSize: '12px'
                                }}>
                                  {(eq.actual_volume || 0).toFixed(2)}
                                </td>
                                <td style={{ 
                                  padding: '8px 12px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb', 
                                  textAlign: 'center',
                                  fontSize: '12px',
                                  ...getVarianceStyle()
                                }}>
                                  {eq.variance > 0 ? `+${(eq.variance || 0).toFixed(2)}` : (eq.variance || 0).toFixed(2)}
                                </td>
                                <td style={{ 
                                  padding: '8px 12px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb', 
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  color: eq.percentage >= 100 ? '#166534' : eq.percentage > 0 ? '#ca8a04' : '#9ca3af',
                                  fontSize: '12px'
                                }}>
                                  {(eq.percentage || 0).toFixed(2)}%
                                </td>
                                <td style={{ 
                                  padding: '8px 12px', 
                                  borderBottom: eqIdx === group.equipment.length - 1 ? `2px solid ${group.config.borderColor}` : '1px solid #e5e7eb'
                                }}></td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Meta Information */}
            {/* {apiMeta && (
              <div style={{ 
                marginTop: '20px', 
                padding: '12px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '6px',
                fontSize: '12px',
                color: '#6b7280'
              }}>
                <strong>Period:</strong> {apiMeta.start_date} → {apiMeta.end_date} | 
                <strong style={{ marginLeft: '12px' }}>Plan Version:</strong> v{apiMeta.version_used} 
                {apiMeta.version_used === apiMeta.latest_version && <span style={{ color: '#059669' }}> (Latest)</span>}
              </div>
            )} */}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
            No summary data available
          </div>
        )}
      </div>
    </div>
  );
};

export default PhysicalVolumeSummary;

